import { releasedCommission, type CommissionBase } from "./commission";
import { addDays, isIsoDate } from "./dates";
import { DomainError } from "./errors";

// ===== Conta-corrente de comissões (T1.11, RF-VEN-08; 05 §6 e §10) =====

/**
 * Tipos de lançamento. Valor gravado com sinal (efeito no saldo do vendedor): `commission` (+) e `commission_reversal`
 * (−) vêm do sistema (liberação e estorno pelo pedido); `credit` (+), `debit` (−) e `payment` (−) são manuais; os
 * `*_reversal` estornam um manual (sinal inverso, nunca DELETE).
 */
export const COMMISSION_ENTRY_KINDS = [
  "commission",
  "commission_reversal",
  "credit",
  "credit_reversal",
  "debit",
  "debit_reversal",
  "payment",
  "payment_reversal",
] as const;
export type CommissionEntryKind = (typeof COMMISSION_ENTRY_KINDS)[number];

/** Lançados à mão no extrato (o pagamento tem rota própria: gera conta a pagar). */
export const MANUAL_COMMISSION_KINDS = ["credit", "debit"] as const;
export type ManualCommissionKind = (typeof MANUAL_COMMISSION_KINDS)[number];
export const isManualCommissionKind = (k: string): k is ManualCommissionKind =>
  (MANUAL_COMMISSION_KINDS as readonly string[]).includes(k);

/** Linhas do resumo do extrato (PRD): comissões, outros créditos, outros débitos e pagamentos. */
export const COMMISSION_BUCKETS = ["commissions", "credits", "debits", "payments"] as const;
export type CommissionBucket = (typeof COMMISSION_BUCKETS)[number];
export const COMMISSION_ENTRY_BUCKET: Readonly<Record<CommissionEntryKind, CommissionBucket>> = {
  commission: "commissions",
  commission_reversal: "commissions",
  credit: "credits",
  credit_reversal: "credits",
  debit: "debits",
  debit_reversal: "debits",
  payment: "payments",
  payment_reversal: "payments",
};

/** Saldo do vendedor: ±1e15 centavos, conferido em BigInt na posição resultante (como o saldo das contas). */
export const MAX_COMMISSION_BALANCE_CENTS = 1_000_000_000_000_000;
/** Lançamentos manuais (crédito, débito e pagamento) por empresa: 422 `limit_reached`. */
export const MAX_MANUAL_COMMISSION_ENTRIES = 200_000;
/** Período do resumo e do extrato. */
export const MAX_COMMISSION_PERIOD_DAYS = 366;
/** Pedidos com comissão pendente listados no extrato (os mais antigos primeiro). */
export const MAX_COMMISSION_PENDING_LIST = 100;

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

const positiveInt = (v: number) => {
  if (!Number.isSafeInteger(v)) fail("not_integer", `valor deve ser inteiro: ${v}`);
  if (v <= 0) fail("invalid_money", `valor deve ser positivo: ${v}`);
};

/** Valor com sinal de um lançamento manual (`amountCents` positivo). */
export function signedEntryAmount(kind: ManualCommissionKind | "payment", amountCents: number): number {
  positiveInt(amountCents);
  return kind === "credit" ? amountCents : -amountCents;
}

const REVERSAL: Readonly<Partial<Record<CommissionEntryKind, CommissionEntryKind>>> = {
  credit: "credit_reversal",
  debit: "debit_reversal",
  payment: "payment_reversal",
};

/** Estorno de um lançamento manual: tipo próprio e sinal inverso. Comissão e estornos não se estornam. */
export function reversalEntry(
  kind: CommissionEntryKind,
  signedAmountCents: number,
): { kind: CommissionEntryKind; amountCents: number } {
  const r = REVERSAL[kind] ?? fail("not_reversible", "Este lançamento não pode ser estornado.");
  return { kind: r, amountCents: -signedAmountCents };
}

/** Estado de um documento de comissão (um ciclo de "lançar contas" do pedido). */
export type CommissionDocState = {
  base: CommissionBase;
  commissionCents: number;
  releasedCents: number;
  /** false = contas estornadas (pedido cancelado): nada fica liberado. */
  active: boolean;
  /** Quitado e valor dos títulos ativos do documento (base "recebido"). */
  paidCents: number;
  totalCents: number;
};

/** Quanto deve estar liberado agora (05 §6): faturado = tudo; recebido = `paid / total` acumulado; cancelado = zero. */
export function releaseTarget(s: CommissionDocState): number {
  if (!s.active) return 0;
  if (s.base === "invoiced") return s.commissionCents;
  return releasedCommission(s.commissionCents, s.paidCents, s.totalCents);
}

/**
 * Lançamento que leva o liberado ao alvo: positivo = `commission`, negativo = `commission_reversal`, zero = null.
 * Idempotente: depois de aplicado, o mesmo estado dá null (repetição de baixa, fila ou clique duplo).
 */
export function releaseDelta(
  s: CommissionDocState,
): { kind: "commission" | "commission_reversal"; amountCents: number } | null {
  const delta = releaseTarget(s) - s.releasedCents;
  if (delta === 0) return null;
  return { kind: delta > 0 ? "commission" : "commission_reversal", amountCents: delta };
}

/** Teto do saldo na posição resultante; o lançamento que aproxima o saldo de zero passa (conserta dado antigo). */
export function assertCommissionBalance(before: bigint, after: bigint): void {
  const max = BigInt(MAX_COMMISSION_BALANCE_CENTS);
  const abs = (v: bigint) => (v < 0n ? -v : v);
  if (abs(after) > max && abs(after) >= abs(before))
    fail("balance_limit", "O saldo de comissões do vendedor passaria do limite permitido.");
}

/** Competência (AAAA-MM) de uma data AAAA-MM-DD. */
export function competenceOf(date: string): string {
  if (!isIsoDate(date)) fail("invalid_date", `data inválida: ${date}`);
  return date.slice(0, 7);
}

/** Primeiro e último dia do mês AAAA-MM. */
export function monthBounds(month: string): { from: string; to: string } {
  const from = `${month}-01`;
  if (!/^\d{4}-\d{2}$/.test(month) || !isIsoDate(from)) fail("invalid_date", `mês inválido: ${month}`);
  const [y, m] = month.split("-").map(Number) as [number, number];
  const next =
    m === 12
      ? `${String(y + 1).padStart(4, "0")}-01-01`
      : `${month.slice(0, 5)}${String(m + 1).padStart(2, "0")}-01`;
  return { from, to: addDays(next, -1) };
}

/** Problema do período (datas já válidas), ou null. */
export function periodProblem(from: string, to: string): string | null {
  if (from > to) return "A data de início deve ser anterior ou igual à data final";
  if (to > addDays(from, MAX_COMMISSION_PERIOD_DAYS - 1))
    return `O período pode ter no máximo ${MAX_COMMISSION_PERIOD_DAYS} dias`;
  return null;
}

/** Pagamento até o saldo atual do vendedor (sem adiantamento). */
export function paymentProblem(amountCents: number, balanceCents: bigint): string | null {
  return BigInt(amountCents) > balanceCents ? "O pagamento passa do saldo de comissões do vendedor." : null;
}

const safe = (v: bigint | null): number | null => {
  if (v === null) return null;
  const n = Number(v);
  return Number.isSafeInteger(n) ? n : null;
};

export type CommissionStatementSummary = {
  openingCents: number | null;
  commissionsCents: number | null;
  creditsCents: number | null;
  /** Positivo: o que saiu do saldo. */
  debitsCents: number | null;
  paymentsCents: number | null;
  closingCents: number | null;
};

/**
 * Resumo do extrato (RF-VEN-08): saldo anterior + comissões + outros créditos − outros débitos − pagamentos = saldo.
 * `sums` são as somas com sinal de cada balde (null = desconhecida, ex.: total() acima de 2^53); cada número que não é
 * seguro vira null na resposta, e o saldo final também quando falta alguma parte.
 */
export function statementSummary(i: {
  openingCents: bigint | null;
  sums: Readonly<Record<CommissionBucket, bigint | null>>;
}): CommissionStatementSummary {
  const parts = [i.openingCents, ...COMMISSION_BUCKETS.map((b) => i.sums[b])];
  const closing = parts.some((p) => p === null) ? null : parts.reduce<bigint>((a, p) => a + p!, 0n);
  const neg = (v: bigint | null) => (v === null ? null : -v);
  return {
    openingCents: safe(i.openingCents),
    commissionsCents: safe(i.sums.commissions),
    creditsCents: safe(i.sums.credits),
    debitsCents: safe(neg(i.sums.debits)),
    paymentsCents: safe(neg(i.sums.payments)),
    closingCents: safe(closing),
  };
}
